'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Play, Calendar, Package, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { workOrders, boms, workstations } from '../data/mock-data';
import {
  workOrderStatusConfig,
  workOrderPriorityConfig,
  operationStatusConfig,
  type WorkOrder,
  type WorkOrderStatus,
  type WorkOrderPriority,
} from '../types';

// 작업지시 등록 모달
function WorkOrderAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [operations, setOperations] = useState([{ operationName: '', workstation: '', plannedTime: 60 }]);

  const addOperation = () => setOperations([...operations, { operationName: '', workstation: '', plannedTime: 60 }]);
  const removeOperation = (index: number) => {
    if (operations.length > 1) setOperations(operations.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="작업지시 등록"
      description="새로운 생산 작업지시를 등록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="작업지시번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="BOM" required>
            <FormSelect>
              <option value="">BOM을 선택하세요</option>
              {boms.filter(b => b.status === 'active').map((bom) => (
                <option key={bom.id} value={bom.id}>{bom.code} - {bom.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="생산 수량" required>
            <FormInput type="number" min={1} placeholder="0" />
          </FormField>
          <FormField label="우선순위" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(workOrderPriorityConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="계획 시작일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="계획 완료일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="목표 창고">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="WH001">완제품 창고</option>
              <option value="WH002">생산 창고</option>
            </FormSelect>
          </FormField>
          <FormField label="판매주문 참조">
            <FormInput placeholder="SO-2024-XXX" />
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">공정 목록</h3>
            <Button variant="outline" size="sm" onClick={addOperation}>
              <Plus className="w-4 h-4 mr-1" />공정 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">공정명</th>
                  <th className="text-left p-3 font-medium w-40">작업장</th>
                  <th className="text-right p-3 font-medium w-32">예정 시간(분)</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {operations.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2"><FormInput placeholder="공정명" /></td>
                    <td className="p-2">
                      <FormSelect>
                        <option value="">선택</option>
                        {workstations.filter(ws => ws.isActive).map((ws) => (
                          <option key={ws.id} value={ws.id}>{ws.name}</option>
                        ))}
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={60} className="text-right" /></td>
                    <td className="p-2">
                      <button onClick={() => removeOperation(index)} className="p-1 rounded hover:bg-accent" disabled={operations.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <FormField label="비고">
          <FormTextarea rows={2} placeholder="작업지시 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function WorkOrderListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [expandedWorkOrder, setExpandedWorkOrder] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return workOrders.filter((wo) => {
      const matchesSearch =
        wo.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wo.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wo.itemCode.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = selectedStatus === 'all' || wo.status === selectedStatus;
      const matchesPriority = selectedPriority === 'all' || wo.priority === selectedPriority;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [searchQuery, selectedStatus, selectedPriority]);

  const toggleExpand = (id: string) => {
    setExpandedWorkOrder(expandedWorkOrder === id ? null : id);
  };

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    workOrders.forEach((wo) => {
      stats[wo.status] = (stats[wo.status] || 0) + 1;
    });
    return stats;
  }, []);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="작업지시 관리"
        description="생산 작업지시를 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            작업지시 등록
          </button>
        }
      />

      {/* 상태별 요약 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(workOrderStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">{statusStats[status] || 0}건</div>
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="작업지시번호, 품목명, 품목코드 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(workOrderStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>

        <select
          value={selectedPriority}
          onChange={(e) => setSelectedPriority(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 우선순위</option>
          {Object.entries(workOrderPriorityConfig).map(([priority, config]) => (
            <option key={priority} value={priority}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 작업지시 목록 */}
      <div className="space-y-4">
        {filteredData.map((wo) => {
          const isExpanded = expandedWorkOrder === wo.id;
          const statusConfig = workOrderStatusConfig[wo.status];
          const priorityConfig = workOrderPriorityConfig[wo.priority];
          const progress = wo.qty > 0 ? (wo.producedQty / wo.qty) * 100 : 0;

          return (
            <div
              key={wo.id}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              {/* 작업지시 헤더 */}
              <div
                className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => toggleExpand(wo.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      wo.priority === 'urgent'
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : 'bg-blue-100 dark:bg-blue-900/30'
                    }`}>
                      <Play className={`w-5 h-5 ${
                        wo.priority === 'urgent'
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-primary">{wo.code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                          {statusConfig.label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(priorityConfig.color)}`}>
                          {priorityConfig.label}
                        </span>
                      </div>
                      <div className="font-medium mt-1">{wo.itemName}</div>
                      <div className="text-xs text-muted-foreground">{wo.itemCode}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">진행률</div>
                      <div className="font-bold">{progress.toFixed(1)}%</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">수량</div>
                      <div className="font-bold">{wo.producedQty} / {wo.qty}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="p-1.5 rounded hover:bg-accent"
                        title="보기"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                        className="p-1.5 rounded hover:bg-accent"
                        title="수정"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>

                {/* 진행률 바 */}
                <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      wo.status === 'completed'
                        ? 'bg-green-500'
                        : wo.status === 'in_progress'
                        ? 'bg-yellow-500'
                        : wo.status === 'stopped'
                        ? 'bg-orange-500'
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* 작업지시 상세 */}
              {isExpanded && (
                <div className="border-t border-border p-4 bg-accent/30">
                  {/* 일정 정보 */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-background border border-border">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        계획 시작일
                      </div>
                      <div className="font-medium mt-1">{wo.plannedStartDate}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-background border border-border">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        계획 완료일
                      </div>
                      <div className="font-medium mt-1">{wo.plannedEndDate}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-background border border-border">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        실제 시작일
                      </div>
                      <div className="font-medium mt-1">{wo.actualStartDate || '-'}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-background border border-border">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        실제 완료일
                      </div>
                      <div className="font-medium mt-1">{wo.actualEndDate || '-'}</div>
                    </div>
                  </div>

                  {/* 추가 정보 */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-background border border-border">
                      <div className="text-xs text-muted-foreground">BOM</div>
                      <div className="font-medium mt-1">{wo.bomCode}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-background border border-border">
                      <div className="text-xs text-muted-foreground">창고</div>
                      <div className="font-medium mt-1">{wo.warehouse}</div>
                    </div>
                    {wo.salesOrderCode && (
                      <div className="p-3 rounded-lg bg-background border border-border">
                        <div className="text-xs text-muted-foreground">판매주문</div>
                        <div className="font-medium mt-1 text-primary">{wo.salesOrderCode}</div>
                      </div>
                    )}
                  </div>

                  {/* 공정 목록 */}
                  <div>
                    <h4 className="font-medium mb-3">공정 진행 현황</h4>
                    <div className="space-y-2">
                      {wo.operations.map((op, index) => {
                        const opStatusConfig = operationStatusConfig[op.status];
                        const opProgress = wo.qty > 0 ? (op.completedQty / wo.qty) * 100 : 0;

                        return (
                          <div
                            key={op.id}
                            className="p-3 rounded-lg bg-background border border-border"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                                  {index + 1}
                                </span>
                                <span className="font-medium">{op.operationName}</span>
                                <span className="text-sm text-muted-foreground">@ {op.workstation}</span>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(opStatusConfig.color)}`}>
                                {opStatusConfig.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-muted-foreground">
                                완료: {op.completedQty} / {wo.qty}
                              </span>
                              <span className="text-muted-foreground">
                                시간: {op.actualTime}분 / {op.plannedTime}분
                              </span>
                            </div>
                            <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  op.status === 'completed'
                                    ? 'bg-green-500'
                                    : op.status === 'in_progress'
                                    ? 'bg-yellow-500'
                                    : 'bg-gray-400'
                                }`}
                                style={{ width: `${opProgress}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 비고 */}
                  {wo.remarks && (
                    <div className="mt-4 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/30">
                      <div className="text-xs text-muted-foreground mb-1">비고</div>
                      <div className="text-sm">{wo.remarks}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          검색 결과가 없습니다.
        </div>
      )}

      {/* 작업지시 등록 모달 */}
      <WorkOrderAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
